import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/StatusBadge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Sparkles, Send } from "lucide-react";
import { toast } from "sonner";

export default function WeeklySummaries() {
  const { user } = useAuth();
  const canSend = ["ADMIN", "MANAGER"].includes(user?.role);
  const canGen = ["ADMIN", "MANAGER", "COORDINATOR"].includes(user?.role);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [summaries, setSummaries] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [sendItem, setSendItem] = useState(null);
  const [recipient, setRecipient] = useState("");
  const [editText, setEditText] = useState("");
  const [startDay, setStartDay] = useState("");

  const load = () => {
    const q = projectId ? `?project_id=${projectId}` : "";
    api.get(`/weekly-summaries${q}`).then((r) => setSummaries(r.data));
  };
  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data));
    api.get("/settings").then((r) => {
      const s = r.data.find((x) => x.key === "weekly_summary_start_day");
      setStartDay(s?.value || "MONDAY");
    });
  }, []);
  useEffect(() => { load(); }, [projectId]);

  const generate = async () => {
    if (!projectId) { toast.error("Select a project"); return; }
    setGenerating(true);
    try {
      await api.post(`/weekly-summaries/generate?project_id=${projectId}&date=${date}`);
      toast.success("Weekly summary generated");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setGenerating(false); }
  };

  const saveEdit = async (s) => {
    try {
      await api.put(`/weekly-summaries/${s.id}`, { edited_client_text: editText });
      toast.success("Client note saved");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doSend = async () => {
    try {
      await api.post(`/weekly-summaries/${sendItem.id}/send`, { recipient_email: recipient });
      toast.success(`Email sent to ${recipient}`);
      setSendItem(null); setRecipient("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
      {canGen && (
        <Card><CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="space-y-1 flex-1 min-w-[180px]">
            <Label>Project</Label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger data-testid="weekly-summary-project-select"><SelectValue placeholder="All projects" /></SelectTrigger>
              <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Any date in the week</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="weekly-summary-date-input" />
          </div>
          <Button onClick={generate} disabled={generating || !projectId} className="rounded-full" data-testid="generate-weekly-summary-button">
            <Sparkles className="h-4 w-4 mr-1" /> {generating ? "Generating…" : "Generate Weekly"}
          </Button>
          {startDay && (
            <p className="w-full text-xs text-muted-foreground">
              Weeks start on <span className="capitalize">{startDay.toLowerCase()}</span> — change this in Settings.
            </p>
          )}
        </CardContent></Card>
      )}

      <div className="space-y-3">
        {summaries.length === 0 ? (
          <p className="text-sm text-muted-foreground">No weekly summaries yet. Generate one above.</p>
        ) : summaries.map((s) => (
          <Card key={s.id} data-testid={`weekly-summary-${s.id}`}>
            <CardContent className="p-5 space-y-3">
              <div className="flex flex-wrap justify-between items-start gap-2">
                <div>
                  <div className="font-heading font-semibold">{s.project?.name}</div>
                  <div className="text-xs font-mono text-muted-foreground">
                    {s.project?.code} · {s.week_start} → {s.week_end} · {s.message_count} msgs · {s.active_days} active days
                  </div>
                </div>
                <StatusBadge status={s.status} />
              </div>
              {s.status === "FAILED" ? (
                <p className="text-sm text-destructive">{s.generation_error}</p>
              ) : (
                <>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Internal Weekly Brief</div>
                    <p className="text-sm whitespace-pre-wrap">{s.internal_text || "—"}</p>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Client-safe Weekly Note</div>
                    {canSend ? (
                      <Textarea rows={4} defaultValue={s.edited_client_text || s.client_text}
                        onFocus={() => setEditText(s.edited_client_text || s.client_text)}
                        onChange={(e) => setEditText(e.target.value)}
                        onBlur={() => (editText && editText !== (s.edited_client_text || s.client_text)) && saveEdit(s)}
                        data-testid={`weekly-client-text-${s.id}`} />
                    ) : (
                      <p className="text-sm whitespace-pre-wrap">{s.edited_client_text || s.client_text || "—"}</p>
                    )}
                  </div>
                  {canSend && s.status !== "NO_ACTIVITY" && (
                    <Button size="sm" className="rounded-full"
                      onClick={() => { setSendItem(s); setRecipient(s.project?.client?.contact_email || ""); }}
                      data-testid={`send-weekly-summary-${s.id}`}>
                      <Send className="h-4 w-4 mr-1" /> Send to Client
                    </Button>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!sendItem} onOpenChange={(o) => !o && setSendItem(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Send weekly client summary</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">The client-safe note is emailed to this address.</p>
          <div className="space-y-1">
            <Label>Recipient Email</Label>
            <Input type="email" value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="client@example.com" data-testid="weekly-send-recipient-input" />
          </div>
          <DialogFooter>
            <Button onClick={doSend} disabled={!recipient} data-testid="confirm-weekly-send-button">Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
